import moment from 'moment'
import { initialClubValues, buildClubCreationPayload, validateClubCreation, clubDays } from './clubCreationForm'
import { clubDayHours, clubClock } from './ClubHoursEditor'
import { CLUB_TEXT_BLOCKS, clubTextPayload, clubTextFormValues } from './clubTexts'

// The editor's values of a club's profile, with its About and Directions as their blocks (`localizedTexts`) hold their originals.
export const initialClubEditValues = (profile, localizedTexts) => {
  const values = { ...initialClubValues(), clubName: profile.clubName || '', businessName: profile.businessName || '',
    country: profile.location?.country || '', location: profile.location || null, sports: profile.sports || [],
    partnerClub: Boolean(profile.partnerClub), isOrganizer: Boolean(profile.isOrganizer),
    amenities: (profile.amenities || []).map(amenity => amenity.id), bookingAccess: profile.bookingAccess || '',
    applicationFee: profile.bookingSettings?.applicationFee ?? '', bookInAdvance: profile.bookingSettings?.bookInAdvance ?? '',
    minCancellationTime: profile.bookingSettings?.minCancellationTime ?? '', notificationsEmails: profile.bookingSettings?.notificationsEmails || '',
    organizerCompany: profile.organizerInfo?.companyName || '', organizerRegistrationNumber: profile.organizerInfo?.uniqueRegistrationNumber || '',
    organizerCountry: profile.organizerInfo?.location?.country || '', organizerLocation: profile.organizerInfo?.location || null,
    organizerOSAccess: profile.organizerInfo?.osAccess === undefined ? '' : String(profile.organizerInfo.osAccess), ownerJobTitle: profile.ownerInfo?.jobTitle || '',
  }
  for (const key of ['description', 'directionsInstructions', 'facebookUrl', 'instagramUrl', 'twitterUrl']) values[key] = profile[key] || ''
  Object.assign(values, clubTextFormValues(localizedTexts))
  for (const prefix of ['owner', 'contact', 'organizer']) for (const key of ['firstName', 'lastName', 'email', 'phone']) {
    values[prefix + key.charAt(0).toUpperCase() + key.slice(1)] = profile[prefix + 'Info']?.[key] || ''
  }
  values.website = profile.contactInfo?.website || ''
  for (const sport of ['tennis', 'padel']) {
    values[sport + 'Courts'] = profile.courtsInfo?.[sport]?.totalCourts ?? ''
    values[sport + 'Surfaces'] = profile.courtsInfo?.[sport]?.courtTypes || []
  }
  for (const day of Object.keys(clubDays)) {
    const entry = clubDayHours(profile.openHours, day)
    values[day + 'State'] = entry.enabled === true ? 'open' : entry.enabled === false ? 'closed' : ''
    values[day + 'Start'] = clubClock(entry, 'start'); values[day + 'End'] = clubClock(entry, 'end')
  }
  values.offerStatus = profile.exclusiveOffer?.status || ''
  for (const side of ['start', 'end']) values['offer' + side + 'Date'] = profile.exclusiveOffer?.[side + 'Date'] ? moment(profile.exclusiveOffer[side + 'Date']).format('YYYY-MM-DD') : ''
  for (const part of ['weekday', 'weekend']) for (const key of ['pricePerHour', 'startDay', 'endDay', 'startHour', 'endHour']) {
    values[part + key] = profile.exclusiveOffer?.[part]?.[key] ?? ''
  }
  return values
}
// Phones accept any legacy text and are resent as stored; only the validated optional links are withheld when unchanged.
const optionalContacts = ['website', 'facebookUrl', 'instagramUrl', 'twitterUrl']
export const validateClubEdit = (values, options, original) => {
  const errors = validateClubCreation(values, options)
  if (original) {
    const initial = initialClubEditValues(original)
    for (const field of optionalContacts) if (values[field] === initial[field]) delete errors[field]
  }
  for (const [key, min, max, integer] of [['applicationFee', 0, 100, false], ['bookInAdvance', 1, 14, true], ['minCancellationTime', 3, 24, true]]) {
    const n = Number(values[key])
    if (values[key] !== '' && (!Number.isFinite(n) || n < min || n > max || (integer && !Number.isInteger(n)))) errors[key] = `Enter a number from ${min} to ${max}.`
  }
  if (values.notificationsEmails && !values.notificationsEmails.split(',').every(value => /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(value.trim()))) errors.notificationsEmails = 'Enter valid email addresses separated by commas.'
  if (values.offerStatus === 'active') {
    for (const side of ['start', 'end']) if (!moment(values['offer' + side + 'Date'], 'YYYY-MM-DD', true).isValid()) errors['offer' + side + 'Date'] = 'Choose a valid offer date.'
    if (values.offerendDate < values.offerstartDate) errors.offerendDate = 'End date must be on or after the start date.'
    for (const part of ['weekday', 'weekend']) {
      const days = part === 'weekday' ? ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] : ['saturday', 'sunday']
      const price = values[part + 'pricePerHour']
      if (price === '' || !Number.isFinite(Number(price)) || Number(price) < 0) errors[part + 'pricePerHour'] = 'Enter a nonnegative price.'
      for (const side of ['start', 'end']) {
        if (!days.includes(values[part + side + 'Day'])) errors[part + side + 'Day'] = 'Choose an available day.'
        if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(values[part + side + 'Hour'])) errors[part + side + 'Hour'] = 'Enter a valid local time.'
      }
      if (days.indexOf(values[part + 'endDay']) < days.indexOf(values[part + 'startDay'])) errors[part + 'endDay'] = 'Last day must follow the first day.'
      if (values[part + 'endHour'] <= values[part + 'startHour']) errors[part + 'endHour'] = 'End time must be later than start time.'
    }
  }
  return errors
}
export const buildClubEditPayload = (values, original = {}) => {
  const result = buildClubCreationPayload(values)
  result.location = Object.fromEntries(['name', 'city', 'country', 'coords', 'placeId'].map(key => [key, values.location[key]]))
  result.ownerInfo.phone = values.ownerPhone
  result.contactInfo.phone = values.contactPhone
  result.contactInfo.website = values.website.trim()
  if (result.organizerInfo) result.organizerInfo.phone = values.organizerPhone
  result.ownerInfo.jobTitle = values.ownerJobTitle.trim()
  for (const key of ['facebookUrl', 'instagramUrl', 'twitterUrl']) result[key] = values[key].trim()
  // Each text as its original, in its language: one the author left as it was changes nothing.
  for (const [name, { field }] of Object.entries(CLUB_TEXT_BLOCKS)) result[field] = clubTextPayload(values, name)
  const initial = initialClubEditValues(original)
  // Omitted optional contacts retain their stored value; an explicit empty value clears it.
  for (const field of optionalContacts) {
    if (values[field] !== initial[field]) continue
    if (field === 'website') delete result.contactInfo.website
    else delete result[field]
  }
  result.bookingAccess = values.bookingAccess
  result.bookingSettings = Object.fromEntries(['applicationFee', 'bookInAdvance', 'minCancellationTime'].filter(key => values[key] !== '').map(key => [key, Number(values[key])]))
  result.bookingSettings.notificationsEmails = values.notificationsEmails.trim()
  result.exclusiveOffer = null
  if (values.offerStatus) {
    result.exclusiveOffer = { status: values.offerStatus }
    if (values.offerStatus === 'active') {
      for (const side of ['start', 'end']) {
        const previous = original.exclusiveOffer?.[side + 'Date'], entered = values['offer' + side + 'Date']
        result.exclusiveOffer[side + 'Date'] = previous && moment(previous).format('YYYY-MM-DD') === entered ? previous : moment(entered, 'YYYY-MM-DD', true).toISOString()
      }
      for (const part of ['weekday', 'weekend']) result.exclusiveOffer[part] = {
        pricePerHour: Number(values[part + 'pricePerHour']), ...Object.fromEntries(['startDay', 'endDay', 'startHour', 'endHour'].map(key => [key, values[part + key]])),
      }
    }
  }
  return result
}
